import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { fadeInOutAnimation } from '@app/core/animations';
import { AuthState } from '@app/core/auth/states/auth.state';
import { App } from '@app/core/models/app.model';
import { AppState } from '@app/core/states/app.state';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { ResponsiveState } from '@app/core/states/responsive.state';
import { NotificationCongratsDialogComponent } from '@app/features/notifications/components/notification-congrats-dialog/notification-congrats-dialog.component';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription, filter, map } from 'rxjs';
import { PlatformSubscriber, SubscribersPerDay } from '../../models/statistics.models';
import {
  FetchConsumptionRequest,
  FetchNewSubscribersRequest,
  FetchNotificationsSentRequest,
  FetchSubscribersCountRequest,
  ResetStatisticsState,
} from '../../states/statistics.actions';
import { StatisticsState } from '../../states/statistics.state';

@Component({
  selector: 'rk-statistics-dashboard',
  templateUrl: './statistics-dashboard.component.html',
  styleUrls: ['./statistics-dashboard.component.scss'],
  animations: [fadeInOutAnimation],
  standalone: false,
})
export class StatisticsDashboardComponent implements OnInit, OnDestroy {
  @Select(AuthState.currentFirstname)
  currentFirstname$: Observable<string>;

  @Select(StatisticsState.subscriberCount)
  subscriberCount$: Observable<PlatformSubscriber[]>;

  @Select(StatisticsState.consumption)
  consumption$: Observable<{ consumption: number; limit: number; use_percent: number }>;

  @Select(StatisticsState.newSubscribers)
  newSubscribers$: Observable<SubscribersPerDay[]>;

  @Select(StatisticsState.notificationsSent)
  notificationsSent$: Observable<SubscribersPerDay[]>;

  @Select(AppState.isLoading)
  isLoading$: Observable<boolean>;

  @Select(StatisticsState.isLoading)
  statisticsStateIsLoading$: Observable<boolean>;

  @Select(AppState.currentAppId)
  currentAppId$: Observable<number>;

  @Select(AppState.currentApp)
  currentApp$: Observable<App>;

  @Select(ResponsiveState.showAppManagerMobileWarning)
  showAppManagerMobileWarning$: Observable<boolean>;

  @Select(AuthState.isAdminOrSuperuser)
  isAdminOrSuperuser$: Observable<boolean>;

  isAdmin = false;

  private readonly subscription = new Subscription();
  shortUserName$: Observable<string>;
  totalSubscribers$: Observable<number>;
  iphonePercentage$: Observable<number>;
  androidPercentage$: Observable<number>;
  iphoneCount$: Observable<number>;
  androidCount$: Observable<number>;
  faApple = faApple;
  faAndroid = faAndroid;
  public currentLocale: string;

  tutorialsTranslationExtraction = [
    marker('notification-dashboard.empty-state.tutorial.card1.title'),
    marker('notification-dashboard.empty-state.tutorial.card1.desc'),
    marker('notification-dashboard.empty-state.tutorial.card1.url'),

    marker('notification-dashboard.empty-state.tutorial.card2.title'),
    marker('notification-dashboard.empty-state.tutorial.card2.desc'),
    marker('notification-dashboard.empty-state.tutorial.card2.url'),
  ];

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly dialog: MatDialog,
  ) {
    this.totalSubscribers$ = this.subscriberCount$.pipe(
      map((subscriberCounts: PlatformSubscriber[]) => {
        if (subscriberCounts) {
          return subscriberCounts.reduce(
            (total, subscriberCount) => total + subscriberCount.count,
            0,
          );
        } else {
          return 0;
        }
      }),
    );

    this.iphonePercentage$ = this.subscriberCount$.pipe(
      filter(subscriberCount => !!subscriberCount),
      map(subscriberCounts => {
        const iphoneDetail = subscriberCounts.find(
          detail => detail.platform === 'iphone',
        );
        const total = subscriberCounts.reduce((sum, detail) => sum + detail.count, 0);

        return iphoneDetail ? Math.round((iphoneDetail.count / total) * 100) : 0;
      }),
    );

    this.androidPercentage$ = this.subscriberCount$.pipe(
      filter(subscriberCount => !!subscriberCount),
      map(subscriberCounts => {
        const androidDetail = subscriberCounts.find(
          detail => detail.platform === 'android',
        );
        const total = subscriberCounts.reduce((sum, detail) => sum + detail.count, 0);

        return androidDetail ? Math.round((androidDetail.count / total) * 100) : 0;
      }),
    );

    this.iphoneCount$ = this.subscriberCount$.pipe(
      filter(subscriberCount => !!subscriberCount),
      map(subscriberCounts => {
        const iphoneDetail = subscriberCounts.find(
          detail => detail.platform === 'iphone',
        );

        return iphoneDetail ? iphoneDetail.count : 0;
      }),
    );

    this.androidCount$ = this.subscriberCount$.pipe(
      filter(subscriberCount => !!subscriberCount),
      map(subscriberCounts => {
        const androidDetail = subscriberCounts.find(
          detail => detail.platform === 'android',
        );

        return androidDetail ? androidDetail.count : 0;
      }),
    );
  }
  ngOnInit() {
    // Navigate to notifications page when currentAppId changes and we're not already on that page
    this.subscription.add(
      this.currentAppId$.subscribe(appId => {
        if (appId && !this.router.url.includes(`/apps/${appId}`)) {
          this.router.navigateByUrl(`/apps/${appId}`);
        }

        this.store.dispatch(new ResetStatisticsState());
        this.store.dispatch(new FetchSubscribersCountRequest());
        this.store.dispatch(new FetchConsumptionRequest());
        this.store.dispatch(new FetchNewSubscribersRequest());
        this.store.dispatch(new FetchNotificationsSentRequest());
      }),
    );

    this.subscription.add(
      this.isAdminOrSuperuser$.subscribe(isAdmin => (this.isAdmin = isAdmin)),
    );

    const hasVisited = localStorage.getItem('hasVisitedMobileManager');
    if (!hasVisited) {
      localStorage.setItem('hasVisitedMobileManager', 'true');
      this.dialog.open(NotificationCongratsDialogComponent, {
        width: '611px',
        backdropClass: 'congrats-backdrop',
      });
    }
  }

  clickEvent(event: string) {
    this.store.dispatch(
      new SetEventProperties({
        name: event,
        component: 'Link',
      }),
    );

    this.store.dispatch(new LogEvent('Link Clicked'));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
